import Axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RemoteApiError } from "../useRemote/types";
import { withMutateAsyncSafe } from "../useRemote/useRemoteMutation";
import type { BookingFavoriteMutationPayload } from "./booking-favorites.types";
import { bookingFavoritesQueryKeys } from "./booking-favorites.query-keys";

/**
 * DELETE /booking-favorites with JSON body (useRemoteMutation does not support bodied DELETE).
 */
export function useRemoteRemoveBookingFavorite() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation<void, RemoteApiError, BookingFavoriteMutationPayload>(
    async (payload: BookingFavoriteMutationPayload) => {
      await Axios.delete<void>("/booking-favorites", { data: payload });
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(bookingFavoritesQueryKeys.all);
        if (variables.floorInventoryId != null) {
          queryClient.invalidateQueries(
            bookingFavoritesQueryKeys.byFloor(variables.floorInventoryId)
          );
        }
      },
      onError: error => {
        const message =
          (error as RemoteApiError)?.response?.data?.message ?? t("_bookingFavorites_errorGeneric");
        snackbar.enqueueSnackbar(message, { variant: "error" });
      }
    }
  );

  return withMutateAsyncSafe(mutation);
}
